import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pin, Search } from "lucide-react";
import { toast } from "sonner";
import { OfficeTaskForm } from "@/components/office/OfficeTaskForm";
import { OfficeTaskDetail } from "@/components/office/OfficeTaskDetail";
import { OfficeAiPanel } from "@/components/office/OfficeAiPanel";

const BUCKETS = [
  { key: "urgent", label: "Urgent", cls: "text-signal-red", border: "border-signal-red/40" },
  { key: "today", label: "Today", cls: "text-signal-amber", border: "border-signal-amber/40" },
  { key: "in_progress", label: "In Progress", cls: "text-signal-blue", border: "border-signal-blue/40" },
  { key: "blocked", label: "Blocked", cls: "text-signal-amber", border: "border-signal-amber/40" },
  { key: "on_hold", label: "On Hold", cls: "text-muted-foreground", border: "border-border" },
  { key: "overdue", label: "Overdue", cls: "text-signal-red", border: "border-signal-red/40" },
  { key: "upcoming", label: "Upcoming", cls: "text-muted-foreground", border: "border-border" },
  { key: "completed", label: "Completed", cls: "text-signal-green", border: "border-signal-green/30" },
];

const CHIP_SLA_CLS = { on_track: "text-signal-green", approaching: "text-signal-amber", at_risk: "text-signal-orange", breached: "text-signal-red" };
const CHIP_SLA_LABEL = { on_track: "on track", approaching: "due soon", at_risk: "at risk", breached: "SLA breached" };

export function TaskChip({ t, onClick, onPin }) {
  return (
    <div className={`relative w-full bg-card border rounded-md px-3 py-2 hover:border-primary/50 transition-colors ${t.overdue ? "border-signal-red/40" : "border-border"}`}
      data-testid={`office-task-${t.task_id}`}>
      <button onClick={onClick} className="w-full text-left">
        <div className="flex items-center justify-between gap-2 pr-6">
          <span className="text-sm font-medium truncate">{t.title}</span>
          <span className="metric text-[10px] text-muted-foreground shrink-0">{t.task_id}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          <span className={t.priority === "critical" || t.priority === "high" ? "text-signal-red" : ""}>{t.priority}</span>
          <span>{t.department}</span>
          {t.due_date && <span className={t.overdue ? "text-signal-red" : ""}>due {t.due_date}{t.due_time ? ` ${t.due_time}` : ""}</span>}
          {t.sla && !t.sla.done && <span className={CHIP_SLA_CLS[t.sla.status]}>{CHIP_SLA_LABEL[t.sla.status]}</span>}
          {t.checklist_progress?.total > 0 && <span>{t.checklist_progress.done}/{t.checklist_progress.total} ✓</span>}
        </div>
      </button>
      {onPin && (
        <button onClick={(e) => { e.stopPropagation(); onPin(t); }} className="absolute top-2 right-2" data-testid={`pin-${t.task_id}`}>
          <Pin className={`w-3.5 h-3.5 ${t.pinned ? "fill-primary text-primary" : "text-muted-foreground"}`} />
        </button>)}
    </div>
  );
}

export default function MyWork() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [quick, setQuick] = useState(null);
  const [results, setResults] = useState(null);
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    const [w, m, u, p, i] = await Promise.all([
      api.get("/office/my-work"), api.get("/office/meta"),
      api.get("/office/directory").catch(() => { toast.error("Could not load the user directory — check permissions"); return { data: [] }; }), api.get("/office/projects"),
      api.get("/office/inbox").catch(() => ({ data: null })),
    ]);
    setData(w.data);
    setMeta(m.data);
    setUsers(u.data);
    setProjects(p.data);
    setInbox(i.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePin = async (t) => {
    await api.post(`/office/tasks/${t.task_id}/${t.pinned ? "unpin" : "pin"}`);
    load();
  };

  const runSearch = async (qk = quick) => {
    const r = await api.get("/office/search", { params: { q: searchQ || undefined, quick: qk || undefined } });
    setQuick(qk);
    setResults(r.data);
  };

  const clearSearch = () => { setResults(null); setQuick(null); setSearchQ(""); };

  return (
    <div className="space-y-5" data-testid="mywork-page">
      <SectionTitle
        right={<Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setFormOpen(true)} data-testid="new-office-task-button">
          <Plus className="w-4 h-4 mr-1" /> New Task</Button>}>
        My Work
      </SectionTitle>
      <OfficeAiPanel />
      <div className="flex flex-wrap items-center gap-2" data-testid="office-search-bar">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-72" placeholder="Search ID, title, assignee, customer, order…" value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} data-testid="office-search-input" />
        </div>
        {[["overdue", "Overdue"], ["blocked", "Blocked"], ["on_hold", "On Hold"], ["due_today", "Due Today"], ["high", "High Priority"], ["pinned", "Pinned"]].map(([k, l]) => (
          <button key={k} onClick={() => runSearch(k)} className={`px-2.5 py-1.5 rounded-md text-xs border transition-colors ${quick === k && results ? "border-primary text-primary" : "border-border text-muted-foreground"}`} data-testid={`quick-${k}`}>{l}</button>))}
        {user?.role === "admin" && (
          <button onClick={() => runSearch("archived")} className={`px-2.5 py-1.5 rounded-md text-xs border transition-colors ${quick === "archived" && results ? "border-primary text-primary" : "border-border text-muted-foreground"}`} data-testid="quick-archived">Archived</button>)}
        {results && <Button size="sm" variant="ghost" onClick={clearSearch} data-testid="search-clear">Back to My Work</Button>}
      </div>
      {results && (
        <div data-testid="search-results">
          <h3 className="text-base font-semibold uppercase tracking-tight mb-2">Search Results ({results.length})</h3>
          {results.length === 0 ? <p className="text-xs text-muted-foreground">No tasks match</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              {results.map((t) => <TaskChip key={t.task_id} t={t} onClick={() => setDetailId(t.task_id)} onPin={togglePin} />)}
            </div>)}
        </div>)}
      {!results && inbox && (inbox.acknowledgements.length + inbox.mentions.length + inbox.reopened.length + inbox.approvals.length) > 0 && (
        <div className="bg-card border border-primary/40 rounded-lg p-4 space-y-2" data-testid="inbox-panel">
          <h3 className="text-base font-semibold uppercase tracking-tight text-primary">Inbox</h3>
          {[["acknowledgements", "Needs acknowledgement"], ["mentions", "Mentions"], ["reopened", "Reopened"], ["approvals", "Awaiting your verification"]].map(([k, label]) => (
            inbox[k].length > 0 && (
              <div key={k}>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label} ({inbox[k].length})</p>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                  {inbox[k].map((t) => <TaskChip key={`${k}-${t.task_id}`} t={t} onClick={() => setDetailId(t.task_id)} onPin={togglePin} />)}
                </div>
              </div>)))}
        </div>)}
      {results ? null : !data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <div className="space-y-5">
          {BUCKETS.map(({ key, label, cls, border }) => (
            <div key={key} data-testid={`bucket-${key}`}>
              <h3 className={`text-base font-semibold uppercase tracking-tight mb-2 ${cls}`}>
                {label} <span className="metric text-sm">({data[key].length})</span>
              </h3>
              {data[key].length === 0 ? <p className="text-xs text-muted-foreground">None</p> : (
                <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 border-l-2 pl-3 ${border}`}>
                  {data[key].map((t) => <TaskChip key={t.task_id} t={t} onClick={() => setDetailId(t.task_id)} onPin={togglePin} />)}
                </div>)}
            </div>
          ))}
        </div>)}
      {meta && (
        <OfficeTaskForm open={formOpen} onOpenChange={setFormOpen} task={null} users={users} projects={projects} meta={meta} onSaved={load} />)}
      <OfficeTaskDetail taskId={detailId} onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
}
